// import "ag-grid-enterprise";
import { useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import {
    AllEnterpriseModule,
    LicenseManager,
    ModuleRegistry,
    ColDef,
    DefaultMenuItem,
    GetContextMenuItemsParams,
    GridOptions,
    MenuItemDef,
    SideBarDef,
    StatusPanelDef,
    ToolPanelDef,
} from "ag-grid-enterprise";
import { useMemo, FC, memo, CSSProperties } from "react";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { oneHGridDarkTheme, oneHGridTheme } from "../gridTheme";
import { uiActions } from "../../../reducers/uiReducer";
import {
    DEFAULT_COLID_DOTS,
    defaultCellProcessor,
    defaultHeaderProcessor,
    exportSelectedOnly,
    getSelectedRowsCustom,
    isOnlySingleRowSelected,
    shouldEnableDeactivateButton,
} from "../../../utils/AGGridUtils";
import { toastError } from "../../../utils/AppUtils";
import { getUATMenuItemsDefinition } from "../UATOrdersUtils/UATOrderUtils";
import CustomGridFooter from "./CustomGridFooter";
import { getUATJourneyMenuItemsDefinition } from "../UATJourneyUtils/UATJourneyUtils";

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(
    "Using_this_{AG_Grid}_Enterprise_key_{AG-100130}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Panaton_Inc.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{18_August_2027}____[v3]_[01]_MTgxODU0MzYwMDAwMA==d4acdf9c1ad58a0522a4585890827fbd",
);

export interface GenericGridColumn extends ColDef {
    field: string;
    cellRenderer(value: any, params?: any): any;
    hide: boolean;
    filterParams: any;
    headerName?: string;
    cellRendererParams?: any;
    filter?: string;
    minWidth?: number;
    maxWidth?: number;
    lockVisible?: boolean;
    checkboxSelection?: boolean;
    headerComponent?: any;
    sortable?: boolean;
    headerComponentParams?: any;
    suppressColumnsToolPanel?: boolean;
    cellStyle?: any;
    sort?: "asc" | "desc" | null;
}

interface IProps {
    columnArray: GenericGridColumn[];
    pageSize: number;
    dataFetchingType?: "gql" | "restApi" | "localData";
    rowData?: any;
    suppressMenuHide?: boolean;
    gridHeightCss?: CSSProperties["height"];
    restApiDataClientSide?: boolean;
    rowDragging?: boolean;
    customGridOptions?: GridOptions;
    turnOffManageColumns?: boolean;
    showOptionMenu?: boolean;
    eventHandlers?: any;
    editLink: string | ((rowData: any) => void);
    disableEditing?: boolean;
    editLinkQueryParams?: any;
    deleteButtonFunc?: (id: number, rowData: any) => Promise<any>;
    deactivateButtonFunc?: (id: number) => Promise<any>;
    deactivateButtonActivationChecker?: string | ((rowData: any) => boolean);
    enableUATOrderActions: boolean;
    enableUATJourneyActions: boolean;
    enableOrderCommentsFlyIn: boolean;
    commentContextMenuAction: (rowData) => void;
    disableRowSelecting?: boolean;
    disableAllRowsSelecting?: boolean;
    showTotalCount?: boolean;
    enablePageSizeSelector: boolean;
    availablePageSizes: number[];
    enableRefreshButton: boolean;
    disableExporting: boolean | undefined;
}

const runEditMenuAction = (editLink: any, params: any, navigate: any, editLinkQueryParams: any): void => {
    if (typeof editLink === "function") {
        editLink(params?.node?.data);
        return;
    }
    navigate({
        pathname: `${editLink}/${params?.node?.data?.id}`,
        search: `?${createSearchParams(editLinkQueryParams)}`,
    });
};

const runDeleteMenuAction = (deleteButtonFunc: any, params: any, dispatch: any): void => {
    if (!deleteButtonFunc) return;
    deleteButtonFunc(params?.node?.data?.id, params?.node?.data).then(() => dispatch(uiActions.setForceRefreshGrid(true)));
};

const runDeactivateMenuAction = (deactivateButtonFunc: any, params: any, dispatch: any): void => {
    if (!deactivateButtonFunc) return;
    deactivateButtonFunc(params?.node?.data?.id)
        .then(() => dispatch(uiActions.setForceRefreshGrid(true)))
        .catch(() => toastError(<FormattedMessage id="grid.error.deactivate" defaultMessage="Could not deactivate. Something went wrong!" />));
};

const buildUATSection = (
    enabled: boolean,
    customContextMenuTrigger: boolean,
    selectedRows: any[],
    getDefinition: (rows: any[]) => MenuItemDef[],
): (MenuItemDef | DefaultMenuItem)[] => {
    if (!enabled) return [];
    if (customContextMenuTrigger || selectedRows.length === 1) {
        return ["separator" as DefaultMenuItem, ...getDefinition(selectedRows)];
    }
    return getDefinition(selectedRows);
};

const AgGrid: FC<IProps> = ({
    eventHandlers,
    dataFetchingType,
    restApiDataClientSide,
    rowDragging,
    customGridOptions,
    columnArray,
    pageSize,
    gridHeightCss,
    rowData,
    suppressMenuHide,
    turnOffManageColumns,
    editLink,
    disableEditing,
    editLinkQueryParams,
    deleteButtonFunc,
    deactivateButtonFunc,
    deactivateButtonActivationChecker,
    enableUATOrderActions,
    enableUATJourneyActions,
    enableOrderCommentsFlyIn,
    commentContextMenuAction,
    disableRowSelecting,
    disableAllRowsSelecting,
    showTotalCount,
    enablePageSizeSelector,
    availablePageSizes,
    enableRefreshButton,
    disableExporting,
}) => {
    const { formatMessage } = useIntl();
    const muiTheme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const defaultSideBar: SideBarDef = useMemo(
        () => ({
            toolPanels: [
                {
                    id: "columns",
                    labelDefault: formatMessage({ id: "aggrid.label.manage-column", defaultMessage: "Manage Columns" }),
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                    toolPanelParams: {
                        suppressRowGroups: true,
                        suppressValues: true,
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressColumnFilter: false,
                        suppressColumnSelectAll: false,
                        suppressColumnExpandAll: true,
                    },
                },
            ] as ToolPanelDef[],
        }),
        [],
    );

    const defaultStatusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(
        () => ({
            statusPanels: [
                {
                    statusPanel: CustomGridFooter,
                    statusPanelParams: {
                        showTotalCount,
                        availablePageSizes,
                        enablePageSizeSelector,
                        enableRefreshButton,
                    },
                },
            ],
        }),
        [],
    );

    const generateGridColumns = useMemo(() => {
        if (columnArray?.length > 0) {
            // const currentFilterModel = gridApi.current?.api.getFilterModel();

            return columnArray.map((el) => {
                // const fieldFilterModel = currentFilterModel?.[el.field];
                // const definedFilterParams = el.filterParams;

                // if (fieldFilterModel) {
                //     if (el?.filterParams?.filterOptions?.length) {
                //         const modifiedFilterOptions = definedFilterParams.filterOptions.filter((e) => e !== fieldFilterModel.type);

                //         modifiedFilterOptions.unshift(fieldFilterModel.type);

                //         definedFilterParams.filterOptions = modifiedFilterOptions;
                //     }
                // }

                return {
                    ...el,
                    cellRenderer:
                        typeof el?.cellRenderer === "function" || typeof el?.cellRenderer !== "string"
                            ? (params) => (typeof el?.cellRenderer === "function" ? el.cellRenderer(params.value, params) : <span>{params.value}</span>)
                            : el.cellRenderer,
                };
            }) as ColDef[];
        }
        return null;
    }, [columnArray]);

    const propsObj: AgGridReactProps = {
        ...eventHandlers,
        defaultColDef: {
            flex: 1,
            minWidth: 100,
            sortable: true,
            filter: true,
            filterParams: {
                maxNumConditions: 1,
            },
        },
        ...((dataFetchingType === "localData" || (dataFetchingType === "restApi" && restApiDataClientSide)) && { rowData }),
        ...(((dataFetchingType === "restApi" && !restApiDataClientSide) || dataFetchingType === "gql") && {
            // serverSideInfiniteScroll: true,
            rowModelType: "serverSide",
            cacheBlockSize: pageSize,
            getRowId: (params) => params.data.id.toString(),
        }),
        suppressMenuHide: suppressMenuHide || false,
        rowSelection: {
            mode: "multiRow",
            enableClickSelection: false,
            headerCheckbox: disableRowSelecting ? !disableRowSelecting : !disableAllRowsSelecting,
            copySelectedRows: true,
            checkboxes: !disableRowSelecting,
        },
        cellSelection: true,
        suppressColumnVirtualisation: true,
        rowDragManaged: !!rowDragging,
        animateRows: !!rowDragging,
        processHeaderForClipboard: defaultHeaderProcessor,
        processCellForClipboard: defaultCellProcessor,
    };

    const defaultCsvExportParams = {
        columnSeparator: "\t",
        suppressQuotes: true,
        processCellCallback: defaultCellProcessor,
        processHeaderCallback: defaultHeaderProcessor,
        ...customGridOptions?.defaultCsvExportParams,
    };

    const defaultExcelExportParams = {
        processCellCallback: defaultCellProcessor,
        processHeaderCallback: defaultHeaderProcessor,
        ...customGridOptions?.defaultExcelExportParams,
    };

    const getMenuItems = (params: GetContextMenuItemsParams) => {
        const selectedRows = getSelectedRowsCustom(params.api);

        if (selectedRows.length === 0) {
            params.node?.setSelected(true);
            selectedRows.push(...getSelectedRowsCustom(params.api));
        }

        if (selectedRows.length === 1 && !params.node?.isSelected()) {
            params.api.deselectAll();
            params.node?.setSelected(true);
            selectedRows.splice(0, 1, ...getSelectedRowsCustom(params.api));
        }

        const getContextMenuItems = customGridOptions?.getContextMenuItems;
        const customContextMenuTrigger = params.column?.getColDef().field === DEFAULT_COLID_DOTS;
        const defaultMenuFunctions: DefaultMenuItem[] = ["copy", "copyWithHeaders", "copyWithGroupHeaders", "separator"];
        const exportMenuFunctions = [
            {
                name: "Export",
                icon: `<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>`,
                subMenu: [
                    {
                        name: "CSV Export",
                        icon: `<span class="ag-icon ag-icon-csv" unselectable="on" role="presentation"></span>`,
                        action: () => {
                            const onlySelected = exportSelectedOnly(params.api);
                            params.api.exportDataAsCsv({
                                onlySelected,
                                columnKeys: params.api
                                    .getAllDisplayedColumns()
                                    .filter((element) => element.getColId() !== DEFAULT_COLID_DOTS && !element.getColDef().context?.suppressCsvExport)
                                    .map((element) => element.getColId()),
                            });
                        },
                    },
                    {
                        name: "Excel Export",
                        icon: `<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>`,
                        action: () => {
                            const onlySelected = exportSelectedOnly(params.api);
                            params.api.exportDataAsExcel({
                                onlySelected,
                                autoConvertFormulas: false, // not to be changed for security reasons.
                                columnKeys: params.api
                                    .getAllDisplayedColumns()
                                    .filter((element) => element.getColId() !== DEFAULT_COLID_DOTS && !element.getColDef().context?.suppressExcelExport)
                                    .map((element) => element.getColId()),
                            });
                        },
                    },
                ],
                disabled: disableExporting,
            },
        ];

        const defs: (MenuItemDef | DefaultMenuItem)[] = [...(customContextMenuTrigger ? [] : defaultMenuFunctions)];

        if (getContextMenuItems) {
            const additionalMenuDefinitions = getContextMenuItems(params);
            // @ts-ignore
            const activeMenuDefinitions = additionalMenuDefinitions.filter((item) => item && !item?.disabled);
            defs.push(...activeMenuDefinitions);
        }

        const shouldAddEditOption = !disableEditing;
        const shouldAddDeleteOption = deleteButtonFunc && isOnlySingleRowSelected(params?.api);
        const shouldAddDeactivateTestProductOption =
            deactivateButtonFunc &&
            isOnlySingleRowSelected(params?.api) &&
            shouldEnableDeactivateButton(params?.node?.data, rowData, deactivateButtonFunc, deactivateButtonActivationChecker);

        if (shouldAddEditOption && (customContextMenuTrigger || selectedRows.length === 1)) {
            defs.push({
                name: "Edit",
                action: () => runEditMenuAction(editLink, params, navigate, editLinkQueryParams),
            });
        }

        if (shouldAddDeleteOption) {
            defs.push({
                name: "Delete",
                action: () => runDeleteMenuAction(deleteButtonFunc, params, dispatch),
            });
        }

        if (shouldAddDeactivateTestProductOption) {
            defs.push({
                name: "Deactivate This Product",
                action: () => runDeactivateMenuAction(deactivateButtonFunc, params, dispatch),
            });
        }

        if (enableOrderCommentsFlyIn && (customContextMenuTrigger || selectedRows.length === 1)) {
            defs.push({
                name: formatMessage({ id: "grid.comments.label.context-option", defaultMessage: "Comments" }),
                action: () => commentContextMenuAction(params?.node?.data),
                disabled: false,
            });
        }

        defs.push(...buildUATSection(enableUATOrderActions, customContextMenuTrigger, selectedRows, getUATMenuItemsDefinition));
        defs.push(...buildUATSection(enableUATJourneyActions, customContextMenuTrigger, selectedRows, getUATJourneyMenuItemsDefinition));

        defs.push(...exportMenuFunctions);

        return defs;
    };

    return (
        <div className="generic-pro-grid" style={{ width: "100%", height: gridHeightCss || "calc(100vh - 200px)" }}>
            <AgGridReact
                {...propsObj}
                {...customGridOptions}
                reactiveCustomComponents
                defaultExcelExportParams={defaultExcelExportParams}
                defaultCsvExportParams={defaultCsvExportParams}
                columnDefs={generateGridColumns}
                defaultColDef={{ suppressHeaderMenuButton: true, sortable: true, unSortIcon: true, resizable: true, filterParams: { maxNumConditions: 1 } }}
                getContextMenuItems={getMenuItems}
                sideBar={turnOffManageColumns ? null : defaultSideBar}
                statusBar={defaultStatusBar}
                tooltipShowDelay={200}
                tooltipInteraction
                selectionColumnDef={{
                    suppressSizeToFit: true,
                    maxWidth: 54,
                    lockPinned: true,
                    pinned: "left",
                }}
                theme={muiTheme.palette.mode === "dark" ? oneHGridDarkTheme : oneHGridTheme}
            />
        </div>
    );
};

export default memo(AgGrid);
